import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Card, Descriptions, Table, Spin, Empty, Steps, Tag } from 'antd'
import {
  fetchMaternityCenterOrderProfile,
  fetchMaternityCenterSchoiceProjects,
  fetchMaternityCenterChanKangProjects,
  fetchMaternityCenterOrderProjectArray,
  fetchMaternityCenterOrderVoucher,
  fetchMaternityCenterOrderFamily,
  fetchMaternityCenterOrderPromotion,
  fetchMaternityCenterOrderGift,
  fetchMaternityCenterAffects,
  fetchMaternityCenterSpecialNote
} from '@/redux/order/maternityCenterOrderSlice'
import { renderTextStr, renderPhoneStr, formatDateTimeStr, formatDayString, renderRightMoney } from '@/utils/renderUtils'
import dayjs from 'dayjs'
import i2Math from '@/utils/i2Math'
import { YES_NO_01_OPTIONS, CURRENT_TYPE_OPTIONS, PAY_STATE_OPTIONS, IS_FULL_PAYMENT_OPTIONS, VOUCHER_GIFT_STATUS_OPTIONS, FREE_SINGLE_STATUS_OPTIONS, getOptionLabel } from '@/views/order/orderList/config/orderTypeConfig'

/** 与派单页一致：开始优先实际入住 momCheckInTime，否则预约入住 momAppointInTime */
function formatMaternityServiceStartDay(detail) {
  if (!detail || typeof detail !== 'object') return '--'
  const raw =
    detail.momCheckInTime && detail.momCheckInTime !== 0
      ? detail.momCheckInTime
      : detail.momAppointInTime
  if (!raw) return '--'
  const s = formatDayString(raw)
  return s === '' ? '--' : s
}

/** 服务结束：离店/离院时间 leaveTime */
function formatMaternityServiceEndDay(detail) {
  if (!detail || typeof detail !== 'object') return '--'
  const raw = detail.leaveTime
  if (!raw) return '--'
  const s = formatDayString(raw)
  return s === '' ? '--' : s
}

/** 套餐服务起止日期（展示用） */
function formatMaternityPackageServiceDateRange(detail) {
  const start = formatMaternityServiceStartDay(detail)
  const end = formatMaternityServiceEndDay(detail)
  if (start === '--' && end === '--') return '--'
  return `${start} ~ ${end}`
}

/** roomPlans：stime/etime 与派单页一致（≤10 位数字按秒级时间戳） */
function dayjsFromRoomPlanTime(t) {
  if (t == null || t === '') return dayjs(null)
  if (typeof t === 'number') {
    return t.toString().length <= 10 ? dayjs.unix(t) : dayjs(t)
  }
  return dayjs(t)
}

/**
 * 单条房间预约相对「今天」的时段（按自然日比较，与派单页入住房间一致）
 * @returns {'past'|'current'|'future'|'unknown'}
 */
function classifyRoomPlanPeriod(r) {
  if (!r || !r.stime || !r.etime) return 'unknown'
  const start = dayjsFromRoomPlanTime(r.stime)
  const end = dayjsFromRoomPlanTime(r.etime)
  if (!start.isValid() || !end.isValid()) return 'unknown'
  if (end.startOf('day').isBefore(start.startOf('day'))) return 'unknown'
  const today = dayjs().startOf('day')
  const endDay = end.startOf('day')
  const startDay = start.startOf('day')
  if (endDay.isBefore(today)) return 'past'
  if (startDay.isAfter(today)) return 'future'
  return 'current'
}

const ROOM_PLAN_PERIOD_TAG = {
  current: { color: 'success', label: '入住中' },
  future: { color: 'processing', label: '待入住' },
  past: { color: 'default', label: '已结束' },
  unknown: { color: 'warning', label: '日期不全' }
}

/** 入住起止自然日天数（含首尾） */
function formatRoomPlanDayCount(r) {
  if (!r || !r.stime || !r.etime) return '--'
  const start = dayjsFromRoomPlanTime(r.stime)
  const end = dayjsFromRoomPlanTime(r.etime)
  if (!start.isValid() || !end.isValid()) return '--'
  if (end.startOf('day').isBefore(start.startOf('day'))) return '--'
  return String(end.startOf('day').diff(start.startOf('day'), 'day') + 1)
}

class OrderDetailContent extends React.Component {
  /** 获取月子中心订单详情 */
  fetchOrderDetailData = () => {
    const { orderId, dispatch } = this.props
    if (!orderId || !dispatch) return
    // 订单主详情
    dispatch(fetchMaternityCenterOrderProfile({ id: orderId, query: {} }))
    // 自选项目列表
    dispatch(fetchMaternityCenterSchoiceProjects({ id: orderId, query: {} }))
    // 产后复原项目列表
    dispatch(fetchMaternityCenterChanKangProjects({ id: orderId, query: {} }))
    // 项目列表（order-total/{id}/projects）
    dispatch(fetchMaternityCenterOrderProjectArray({ id: orderId, query: {} }))
    // 代金券
    dispatch(fetchMaternityCenterOrderVoucher({ id: orderId, query: {} }))
    // 家属信息
    dispatch(fetchMaternityCenterOrderFamily({ id: orderId, query: {} }))
    // 促销信息
    dispatch(fetchMaternityCenterOrderPromotion({ id: orderId, query: {} }))
    // 赠品
    dispatch(fetchMaternityCenterOrderGift({ id: orderId, query: {} }))
    // 预订单操作记录
    dispatch(fetchMaternityCenterAffects({ id: orderId, query: {} }))
    // 特别声明内容（与订单 ID 无强绑定，这里保持同一时机拉取）
    dispatch(fetchMaternityCenterSpecialNote({ query: {} }))
  }

  componentDidMount() {
    this.fetchOrderDetailData()
  }

  componentDidUpdate(prevProps) {
    const { orderId } = this.props
    if (orderId && orderId !== prevProps.orderId) {
      this.fetchOrderDetailData()
    }
  }

  /** 月子中心订单详情：按 API 返回结构展示 */
  renderDetail(orderDetail) {
    const { maternityCenterChanKangProjects, projectArrayObj, voucherObj, familyObj, promotionObj, giftObj, affectsObj } = this.props
    const givingProjectList = maternityCenterChanKangProjects?.givingProjectList || []
    const projectArray = maternityCenterChanKangProjects?.projectArray || []
    if (!orderDetail || typeof orderDetail !== 'object') {
      return null
    }
    const givingProjectTotalMoney = givingProjectList.reduce((sum, r) => {
      const n = Number(r?.projectMoney)
      return i2Math.i2Add(sum, Number.isFinite(n) ? n : 0)
    }, 0)

    const affectsRaw = Array.isArray(affectsObj?.data) ? affectsObj.data : []
    // 操作记录倒序：最新在上（不修改 Redux 原数组）
    const affects = [...affectsRaw].reverse()

    // 促销、礼品、代金券、家属：使用独立 API 数据源
    const promotionList = Array.isArray(promotionObj?.data) ? promotionObj.data : []
    const giftList = Array.isArray(giftObj?.data) ? giftObj.data : []
    const voucherList = Array.isArray(voucherObj?.data) ? voucherObj.data : []
    const familyList = Array.isArray(familyObj?.data) ? familyObj.data : []

    // 注意：解构默认值只对 undefined 生效，接口字段可能返回 null
    const {
      maternalInfo = {},
      orderState = [],
      signInfo = [],
      roomPlans = [],
      orderChangeInfo = {},
      sendMoney = {}
    } = orderDetail

    const promotionTotalMoney = promotionList.reduce((sum, r) => {
      const n = Number(r?.promotionCost)
      return i2Math.i2Add(sum, Number.isFinite(n) ? n : 0)
    }, 0)

    const giftTotalMoney = giftList.reduce((sum, r) => {
      const cost = Number(r?.giftCost)
      const num = Number(r?.num)
      const itemTotal = i2Math.i2Multiply(Number.isFinite(cost) ? cost : 0, Number.isFinite(num) ? num : 0)
      return i2Math.i2Add(sum, itemTotal)
    }, 0)

    const voucherGiftTotalMoney = voucherList.reduce((sum, r) => {
      const denom = Number(r?.denomination)
      const num = Number(r?.number)
      const itemTotal = i2Math.i2Multiply(Number.isFinite(denom) ? denom : 0, Number.isFinite(num) ? num : 0)
      return i2Math.i2Add(sum, itemTotal)
    }, 0)

    const hasOrderChange = orderChangeInfo && (orderChangeInfo.id || orderChangeInfo.typeName)
    const hasSendMoney = sendMoney && (sendMoney.id || sendMoney.money != null)

    const continuedOrderTotals = Array.isArray(orderDetail.continuedOrderTotals) ? orderDetail.continuedOrderTotals : []
    const hasContinuedOrderTotals = continuedOrderTotals.length > 0

    return (
      <div>
        {/* 订单基本信息 */}
        <Card title={`${renderTextStr(maternalInfo?.name)} 订单详情`} style={{ marginBottom: '16px' }}>
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="订单ID">{renderTextStr(orderDetail.id)}</Descriptions.Item>
            <Descriptions.Item label="订单类型">{renderTextStr(orderDetail.orderTypeText)}</Descriptions.Item>
            <Descriptions.Item label="合同编号">{renderTextStr(orderDetail.paperNumber)}</Descriptions.Item>
            <Descriptions.Item label="创建时间">{formatDateTimeStr(orderDetail.createTime)}</Descriptions.Item>
            <Descriptions.Item label="签约人">{renderTextStr(orderDetail.userName)}</Descriptions.Item>
            <Descriptions.Item label="签约时间">{formatDateTimeStr(orderDetail.signTime)}</Descriptions.Item>
            <Descriptions.Item label="是否一对一服务">
              {getOptionLabel(YES_NO_01_OPTIONS, orderDetail.individualService)}
            </Descriptions.Item>
            <Descriptions.Item label="使用E卡">
              {getOptionLabel(YES_NO_01_OPTIONS, orderDetail.useEka)}
            </Descriptions.Item>
            <Descriptions.Item label="财务审核"> {renderTextStr(orderDetail.financialAuditText)}</Descriptions.Item>
            <Descriptions.Item label="当前类型">
              {getOptionLabel(CURRENT_TYPE_OPTIONS, orderDetail.nowType)}
            </Descriptions.Item>
            {orderDetail.remark && (
              <Descriptions.Item label="备注" span={2}>{orderDetail.remark}</Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* 套餐与费用 */}
        <Card title="套餐与费用" style={{ marginBottom: '16px' }}>
          <Descriptions title="套餐信息" bordered column={2} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="套餐名称">{renderTextStr(orderDetail.packageName)}</Descriptions.Item>
            <Descriptions.Item label="套餐金额">
              <div style={{ textAlign: 'right' }}>{renderRightMoney(orderDetail.packageMoney)}</div>
            </Descriptions.Item>
            <Descriptions.Item label="套餐服务日期">{formatMaternityPackageServiceDateRange(orderDetail)}</Descriptions.Item>
            <Descriptions.Item label="套餐总天数">{renderTextStr(orderDetail.totalServiceDay)}</Descriptions.Item>
          </Descriptions>
          <Descriptions
            title="赠送"
            bordered
            column={2}
            size="small"
            style={{ marginBottom: hasContinuedOrderTotals ? 0 : 16 }}
          >
            <Descriptions.Item label="赠送天数">{renderTextStr(orderDetail.giveDay)}</Descriptions.Item>
          </Descriptions>
          {hasContinuedOrderTotals ? (
            <Descriptions
              title="续住信息"
              column={2}
              size="small"
              style={{ marginBottom: 16, width: '100%', minWidth: 0 }}
              contentStyle={{ minWidth: 0 }}
            >
              <Descriptions.Item span={2}>
                {/* 约束 flex 子项宽度；overflow-x 将宽表限制在 Card 内横向滚动 */}
                <div style={{ width: '100%', minWidth: 0, maxWidth: '100%', overflowX: 'auto' }}>
                  <Table
                    className="report-table"
                    size="small"
                    pagination={false}
                    dataSource={continuedOrderTotals}
                    rowKey={(r) => (r.id != null ? String(r.id) : `${r.contractNum ?? ''}-${r.signTime ?? ''}`)}
                    scroll={{ x: 916 }}
                    columns={[
                      { title: '合同编号', dataIndex: 'contractNum', key: 'contractNum', width: 120, render: (t) => renderTextStr(t) },
                      { title: '纸质编号', dataIndex: 'paperNumber', key: 'paperNumber', width: 100, render: (t) => renderTextStr(t) },
                      { title: '房型', dataIndex: 'roomTypeName', key: 'roomTypeName', width: 100, render: (t) => renderTextStr(t) },
                      { title: '服务天数', dataIndex: 'serviceDay', key: 'serviceDay', width: 88, render: (t) => renderTextStr(t) },
                      {
                        title: '续住金额',
                        dataIndex: 'continuedLiveMoney',
                        key: 'continuedLiveMoney',
                        width: 100,
                        align: 'right',
                        render: (v) => <div style={{ textAlign: 'right' }}>{renderRightMoney(v)}</div>
                      },
                      { title: '签约时间', dataIndex: 'signTime', key: 'signTime', width: 120, render: (t) => formatDayString(t) },
                      { title: '签约人', dataIndex: 'signUser', key: 'signUser', width: 88, render: (t) => renderTextStr(t) },
                      {
                        title: '备注',
                        dataIndex: 'remark',
                        key: 'remark',
                        width: 200,
                        ellipsis: true,
                        render: (t) => renderTextStr(t)
                      }
                    ]}
                  />
                </div>
              </Descriptions.Item>
            </Descriptions>
          ) : null}
          <Descriptions title="付款信息" bordered column={2} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="是否全款">
              {getOptionLabel(IS_FULL_PAYMENT_OPTIONS, orderDetail.isTotalMoney)}
            </Descriptions.Item>
            <Descriptions.Item label="意向金">
              <div style={{ textAlign: 'right' }}>{renderRightMoney(orderDetail.intentionMoney)}</div>
            </Descriptions.Item>
            <Descriptions.Item label={orderDetail.isTotalMoney == 1 ? '定金' : `定金 (${getOptionLabel(PAY_STATE_OPTIONS, orderDetail.earlyPayState)})`}>
              <div style={{ textAlign: 'right' }}>
                {orderDetail.isTotalMoney == 1 ? '无需' : renderRightMoney(orderDetail.earlyPay)}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="尾款状态">
              {orderDetail.isTotalMoney == 1 ? '无需' : getOptionLabel(PAY_STATE_OPTIONS, orderDetail.retainageState)}
            </Descriptions.Item>
          </Descriptions>
          <Descriptions title="免单与附加" bordered column={2} size="small">
            <Descriptions.Item label="免单金额">
              <div style={{ textAlign: 'right' }}>{renderRightMoney(orderDetail.freeSheetMoney)}</div>
            </Descriptions.Item>
            <Descriptions.Item label="免单状态">
              {getOptionLabel(FREE_SINGLE_STATUS_OPTIONS, orderDetail.freeSingleStatus)}
            </Descriptions.Item>
            <Descriptions.Item label="折扣">{renderTextStr(orderDetail.discount)}</Descriptions.Item>
            <Descriptions.Item label="炖品数量">{renderTextStr(orderDetail.stewNum)}</Descriptions.Item>
          </Descriptions>
        </Card>

        {/* 产妇信息 */}
        <Card title="产妇信息" style={{ marginBottom: '16px' }}>
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="姓名">{renderTextStr(maternalInfo?.name)}</Descriptions.Item>
            <Descriptions.Item label="电话">{renderPhoneStr(maternalInfo?.phone)}</Descriptions.Item>
            <Descriptions.Item label="生日">{renderTextStr(maternalInfo?.birthday)}</Descriptions.Item>
            <Descriptions.Item label="预产期/生产日期">{renderTextStr(maternalInfo?.childBirth)}</Descriptions.Item>
            <Descriptions.Item label="医院">{renderTextStr(maternalInfo?.hospital)}</Descriptions.Item>
            <Descriptions.Item label="胎数">
              {maternalInfo?.fetalNumber != null
                ? maternalInfo.fetalNumber
                : renderTextStr(maternalInfo?.fetalNumberText)}
            </Descriptions.Item>
            <Descriptions.Item label="地址">{renderTextStr(maternalInfo?.address)}</Descriptions.Item>
            <Descriptions.Item label="家庭地址">{renderTextStr(maternalInfo?.familyAdd)}</Descriptions.Item>
          </Descriptions>
        </Card>

        {/* 家属列表 */}
        <Card title="家属列表" style={{ marginBottom: '16px' }}>
          <Table
            className="report-table"
            dataSource={familyList}
            rowKey={(r) => r.id ?? `${r.phone}-${r.name}-${r.relation}`}
            pagination={false}
            size="small"
            columns={[
              { title: '姓名', dataIndex: 'name', key: 'name', render: (t) => renderTextStr(t) },
              { title: '电话', dataIndex: 'phone', key: 'phone', render: (t) => renderPhoneStr(t) },
              { title: '关系', dataIndex: 'relation', key: 'relation', render: (t) => renderTextStr(t) }
            ]}
          />
        </Card>
        {/* 房间预约：roomPlans 项含 name、stime、etime（与派单页入住房间一致） */}
        <Card title="房间列表" style={{ marginBottom: '16px' }}>
          <div style={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
            <Table
              className="report-table"
              dataSource={roomPlans}
              rowKey={(r) => r.id ?? `${r.name ?? ''}-${r.stime}-${r.etime}`}
              pagination={false}
              size="small"
              scroll={{ x: 640 }}
              columns={[
                { title: '房间名称', dataIndex: 'name', key: 'name', render: (t) => renderTextStr(t) },
                { title: '入住开始', dataIndex: 'stime', key: 'stime', render: (t) => formatDayString(t) },
                { title: '入住结束', dataIndex: 'etime', key: 'etime', render: (t) => formatDayString(t) },
                {
                  title: '入住状态',
                  key: 'stayStatus',
                  width: 96,
                  render: (_, r) => {
                    const period = classifyRoomPlanPeriod(r)
                    const cfg = ROOM_PLAN_PERIOD_TAG[period] || ROOM_PLAN_PERIOD_TAG.unknown
                    return <Tag color={cfg.color}>{cfg.label}</Tag>
                  }
                },
                { title: '天数', key: 'days', width: 72, render: (_, r) => formatRoomPlanDayCount(r) }
              ]}
            />
          </div>
        </Card>

        {/* 订单状态/款项 */}
        <Card title="订单状态/款项" style={{ marginBottom: '16px' }}>
          <Table
            className="report-table"
            dataSource={orderState}
            rowKey={(r) => r.id ?? `${r.orderNum}-${r.orderDate}-${r.money}`}
            pagination={false}
            size="small"
            columns={[
              { title: '订单号', dataIndex: 'orderNum', key: 'orderNum', render: (t) => renderTextStr(t) },
              { title: '金额', dataIndex: 'money', key: 'money', render: (v) => renderRightMoney(v) },
              { title: '已收金额', dataIndex: 'gatherMoney', key: 'gatherMoney', render: (v) => renderRightMoney(v) },
              { title: '状态', dataIndex: 'stateName', key: 'stateName', render: (t) => renderTextStr(t) },
              { title: '订单日期', dataIndex: 'orderDate', key: 'orderDate', render: (t) => renderTextStr(t) },
              { title: '支付时间', dataIndex: 'payTime', key: 'payTime', render: (v) => formatDateTimeStr(v) },
              { title: '创建时间', dataIndex: 'createTime', key: 'createTime', render: (v) => formatDateTimeStr(v) },
              { title: '类型', dataIndex: 'typeName', key: 'typeName', render: (t) => renderTextStr(t) }
            ]}
          />
        </Card>

        {/* 签约信息 */}
        <Card title="签约信息" style={{ marginBottom: '16px' }}>
          <Table
            className="report-table"
            dataSource={signInfo}
            rowKey={(r) => r.id ?? `${r.signUserName}-${r.signTime}`}
            pagination={false}
            size="small"
            columns={[
              { title: '签约人', dataIndex: 'signUserName', key: 'signUserName', render: (t) => renderTextStr(t) },
              { title: '签约比例', dataIndex: 'proportion', key: 'proportion', render: (v) => renderTextStr(v) },
              { title: '签约时间', dataIndex: 'signTime', key: 'signTime', render: (v) => formatDateTimeStr(v) || renderTextStr(v) },
              { title: '创建人', dataIndex: 'createUserName', key: 'createUserName', render: (t) => renderTextStr(t) },
              { title: '创建时间', dataIndex: 'createTime', key: 'createTime', render: (v) => formatDateTimeStr(v) || renderTextStr(v) }
            ]}
          />
        </Card>
        {/* 项目明细：产康类 + type1/2/3 */}
        <Card title="项目明细" style={{ marginBottom: '16px' }}>
          {/* 产康类：来自 maternityCenterChanKangProjects.projectArray */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>产康类</div>
            <Table
              className="report-table"
              dataSource={projectArray}
              rowKey={(r) => r.id ?? `${r.projectName}-${r.num}-${r.typeName}`}
              pagination={false}
              size="small"
              columns={[
                { title: '序号', key: 'index', width: 56, align: 'center', render: (_, __, index) => index + 1 },
                { title: '项目名称', dataIndex: 'projectName', key: 'projectName', render: (t) => renderTextStr(t) },
                { title: '项目类型', dataIndex: 'typeName', key: 'typeName', render: (t) => renderTextStr(t) },
                { title: '购买/可用', key: 'numUseNum', render: (_, r) => `${renderTextStr(r.num)} / ${renderTextStr(r.useNum)}` },
                { title: '执行人', dataIndex: 'actionUserName', key: 'actionUserName', render: (t) => renderTextStr(t) },
                // { title: '执行时间', dataIndex: 'actionTime', key: 'actionTime', width: 160, render: (v) => formatDateTimeStr(v) || renderTextStr(v) },
                { title: '佣金', dataIndex: 'commissionMoney', key: 'commissionMoney', render: (v) => renderRightMoney(v) },
              ]}
            />
          </div>
          {/* type1、type2、type3：来自 projectArrayObj */}
          {[projectArrayObj?.type1, projectArrayObj?.type2, projectArrayObj?.type3].map((arr, idx) => {
            const list = Array.isArray(arr) ? arr : []
            if (list.length === 0) return null
            const title = renderTextStr(list[0]?.projectTypeText) || `类型${idx + 1}`
            return (
              <div key={idx} style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8, fontWeight: 500 }}>{title}</div>
                <Table
                  className="report-table"
                  dataSource={list}
                  rowKey={(r) => r.id ?? `${r.projectName}-${r.num}-${r.typeName}-${idx}`}
                  pagination={false}
                  size="small"
                  columns={[
                    { title: '序号', key: 'index', width: 56, align: 'center', render: (_, __, index) => index + 1 },
                    { title: '项目名称', dataIndex: 'projectName', key: 'projectName', render: (t) => renderTextStr(t) },
                    { title: '项目类型', dataIndex: 'typeText', key: 'typeName', render: (t) => renderTextStr(t) },
                    { title: '购买/可用', key: 'numUseNum', render: (_, r) => `${renderTextStr(r.num)} / ${renderTextStr(r.useNum)}` },
                    { title: '执行人', dataIndex: 'actionUserName', key: 'actionUserName', render: (t) => renderTextStr(t) },
                    // { title: '执行时间', dataIndex: 'actionTime', key: 'actionTime', width: 160, render: (v) => formatDateTimeStr(v) || renderTextStr(v) },
                    { title: '佣金', dataIndex: 'commissionMoney', key: 'commissionMoney', render: (v) => renderRightMoney(v) },
                  ]}
                />
              </div>
            )
          })}
        </Card>

        {/* 赠送项目：来自 maternityCenterChanKangProjects.givingProjectList（typeName 含「赠送」） */}
        <Card
          title="赠送项目"
          extra={<span>赠送总额：{renderRightMoney(givingProjectTotalMoney)}</span>}
          style={{ marginBottom: '16px' }}
        >
          <Table
            className="report-table"
            dataSource={givingProjectList}
            rowKey={(r) => r.id ?? `${r.projectName}-${r.typeName}-${r.storeName}`}
            pagination={false}
            size="small"
            columns={[
              { title: '序号', key: 'index', width: 56, align: 'center', render: (_, __, index) => index + 1 },
              { title: '项目名称', dataIndex: 'projectName', key: 'projectName', render: (t) => renderTextStr(t) },
              { title: '项目类型', dataIndex: 'typeName', key: 'typeName', render: (t) => renderTextStr(t) },
              { title: '项目金额', dataIndex: 'projectMoney', key: 'projectMoney', align: 'right', render: (v) => renderRightMoney(v) },
              { title: '门店', dataIndex: 'storeName', key: 'storeName', width: 80, render: (t) => renderTextStr(t) },
              { title: '购买/可用', key: 'numUseNum', render: (_, r) => `${renderTextStr(r.num)} / ${renderTextStr(r.useNum)}` },
              { title: '执行人', dataIndex: 'actionUserName', key: 'actionUserName', render: (t) => renderTextStr(t) || '-' },
              // { title: '执行时间', dataIndex: 'actionTime', key: 'actionTime', width: 160, render: (v) => v ? formatDateTimeStr(v) : renderTextStr(v) }
            ]}
          />
        </Card>

        {/* 促销 */}
        <Card
          title="促销"
          extra={<span>促销总额：{renderRightMoney(promotionTotalMoney)}</span>}
          style={{ marginBottom: '16px' }}
        >
          <Table
            className="report-table"
            dataSource={promotionList}
            rowKey={(r) => r.id ?? `${r.promotionName}-${r.promotionCost}`}
            pagination={false}
            size="small"
            columns={[
              { title: '促销名称', dataIndex: 'promotionName', key: 'promotionName', render: (t) => renderTextStr(t) },
              { title: '促销费用', dataIndex: 'promotionCost', key: 'promotionCost', render: (v) => renderRightMoney(v) }
            ]}
          />
        </Card>

        {/* 礼品 */}
        <Card
          title="礼品"
          extra={<span>礼品总额：{renderRightMoney(giftTotalMoney)}</span>}
          style={{ marginBottom: '16px' }}
        >
          <Table
            className="report-table"
            dataSource={giftList}
            rowKey={(r) => r.id ?? `${r.giftName}-${r.giftCost}-${r.num}`}
            pagination={false}
            size="small"
            columns={[
              { title: '礼品名称', dataIndex: 'giftName', key: 'giftName', render: (t) => renderTextStr(t) },
              { title: '费用', dataIndex: 'giftCost', key: 'giftCost', render: (v) => renderRightMoney(v) },
              { title: '数量', dataIndex: 'num', key: 'num', render: (v) => renderTextStr(v) }
            ]}
          />
        </Card>

        {/* 代金券 */}
        <Card
          title="代金券"
          extra={<span>代金券总额：{renderRightMoney(voucherGiftTotalMoney)}</span>}
          style={{ marginBottom: '16px' }}
        >
          <Table
            className="report-table"
            dataSource={voucherList}
            rowKey={(r) => r.id ?? `${r.voucherName}-${r.denomination}-${r.number}`}
            pagination={false}
            size="small"
            columns={[
              { title: '代金券名称', dataIndex: 'voucherName', key: 'voucherName', render: (t) => renderTextStr(t) },
              { title: '面额', dataIndex: 'denomination', key: 'denomination', render: (v) => renderRightMoney(v) },
              { title: '数量', dataIndex: 'number', key: 'number', render: (v) => renderTextStr(v) },
              { title: '套餐名称', dataIndex: 'packageName', key: 'packageName', render: (t) => renderTextStr(t) },
              { title: '套餐金额', dataIndex: 'packageMoney', key: 'packageMoney', render: (v) => renderRightMoney(v) },
              { title: '状态', dataIndex: 'status', key: 'status', render: (v) => getOptionLabel(VOUCHER_GIFT_STATUS_OPTIONS, v) }
            ]}
          />
        </Card>

        {/* 订单变更 */}
        {hasOrderChange && (
          <Card title="订单变更" style={{ marginBottom: '16px' }}>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="类型">{renderTextStr(orderChangeInfo.typeName)}</Descriptions.Item>
              <Descriptions.Item label="备注" span={2}>{renderTextStr(orderChangeInfo.remark)}</Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {/* 发放金额 */}
        {hasSendMoney && (
          <Card title="发放金额" style={{ marginBottom: '16px' }}>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="操作人">{renderTextStr(sendMoney.operationName)}</Descriptions.Item>
              <Descriptions.Item label="金额">{renderRightMoney(sendMoney.money)}</Descriptions.Item>
              <Descriptions.Item label="发放金额">{renderRightMoney(sendMoney.sendMoney)}</Descriptions.Item>
              <Descriptions.Item label="门店">{renderTextStr(sendMoney.storeName)}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{formatDateTimeStr(sendMoney.createTime) || renderTextStr(sendMoney.createTime)}</Descriptions.Item>
              <Descriptions.Item label="审核时间">{formatDateTimeStr(sendMoney.checkTime) || renderTextStr(sendMoney.checkTime)}</Descriptions.Item>
            </Descriptions>
          </Card>
        )}
        {/* 操作流程 */}
        <Card title="操作流程" style={{ marginBottom: '16px' }}>
          {affects.length ? (
            <Steps
              direction="vertical"
              size="small"
              current={affects.length}
              items={affects.map((r, idx) => ({
                key: r?.id ?? `${idx}-${r?.createdTime ?? ''}-${r?.createdTime ?? ''}`,
                title: renderTextStr(r?.typeName),
                subTitle: r?.createdTime ? formatDateTimeStr(r.createdTime) : '-',
                description: (
                  <div>
                    <div>操作人：{renderTextStr(r?.userName)}</div>
                  </div>
                )
              }))}
            />
          ) : (
            <Empty description="暂无操作记录" />
          )}
        </Card>
      </div>
    )
  }

  render() {
    const { orderId, orderDetailObj } = this.props
    const { fetching, data: orderDetail } = orderDetailObj || {}
    console.log('orderDetailObj', this.props)
    // 无订单 ID 不渲染详情
    if (!orderId) {
      return (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Empty description="请选择订单" />
        </div>
      )
    }

    // 请求中：显示加载
    if (fetching) {
      return (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      )
    }

    // 无数据或数据与当前 orderId 不一致（避免展示上一单的缓存数据）
    const dataMatchesOrder = orderDetail && String(orderDetail.id) === String(orderId)
    if (!dataMatchesOrder) {
      return (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Empty description="订单不存在或加载失败" />
        </div>
      )
    }

    return this.renderDetail(orderDetail)
  }
}

// 使用函数组件作为wrapper来使用React Redux Hooks
const OrderDetailContentWrapper = (props) => {
  const dispatch = useDispatch()
  const orderState = useSelector(state => state.order?.order || {})
  const {
    detail: orderDetailObj,
    maternityCenterChanKangProjects,
    maternityCenterOrderProjectArray: projectArrayObj,
    maternityCenterOrderVoucher: voucherObj,
    maternityCenterOrderFamily: familyObj,
    maternityCenterOrderPromotion: promotionObj,
    maternityCenterOrderGift: giftObj,
    maternityCenterAffects: affectsObj
  } = orderState || {}

  const enhancedProps = {
    dispatch,
    orderDetailObj,
    maternityCenterChanKangProjects,
    projectArrayObj,
    voucherObj,
    familyObj,
    promotionObj,
    giftObj,
    affectsObj,
    ...props
  }

  return <OrderDetailContent {...enhancedProps} />
}

export default OrderDetailContentWrapper
